/* ============================================================
   BUSINESS IDENTITY — resolve the name a report cover should carry.

   The sign-in response never includes the business name; only
   GET /api/auth/user returns it (Businesses.name, exposed as
   business.businessName). Nothing else in the app writes the
   `businessName` localStorage key, so any cover that read it
   directly always fell through to the raw "BID 136" fallback.

   This module reads every cached source first, falls back to the
   one endpoint that actually knows, and caches the answer so the
   next export is instant.

   The cache is keyed by business id. `businessName` is a single
   global localStorage key with no owner of its own, so a name
   cached under one account used to be printed on every later
   account's covers — a session that ended without an explicit
   logout (token expiry, closed tab, a sign-in that bypasses
   AuthContext) left the previous tenant's name behind for good.
   Every read now checks the id the name was stored against and
   discards it on a mismatch, which also self-heals the stale
   entries already sitting in users' browsers.
   ============================================================ */
import http from '../services/httpClient';

const clean = (value) => String(value == null ? '' : value).trim();

/* Sign-up auto-names a business "<Owner>'s Business (<email>)". Strip that
   scaffolding so a cover reads with the real trading name where one exists. */
export const normalizeBusinessName = (value) => clean(value)
  .replace(/\s*\([^)]*@[^)]*\)\s*$/, '')
  .replace(/'s\s*Business$/i, '')
  .trim();

export const getBusinessId = () => {
  if (typeof window === 'undefined') return '';
  try {
    return clean(window.localStorage.getItem('businessId'));
  } catch (_error) {
    return '';
  }
};

/* Keys different parts of the app have historically written the name under. */
const NAME_KEYS = ['businessName', 'restaurantName', 'companyName', 'business_name', 'restaurant_name'];

/* Which business the keys above were last written for. Absent on caches
   written before this key existed, which is treated as "unknown owner". */
const NAME_OWNER_KEY = 'businessNameId';

const readLocal = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (_error) {
    return null;
  }
};

const readStoredUser = () => {
  const raw = readLocal('user');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    // Corrupted user blob — callers fall through to their remaining sources.
    return null;
  }
};

/* The business the current session is signed in as, per the stored user blob.
   Sign-in rewrites that blob wholesale, so unlike the loose name keys it is
   never left over from a previous account. */
const getSignedInBusinessId = () => {
  const user = readStoredUser();
  if (!user) return '';
  return clean(
    user.businessId
    || user.business_id
    || user.businessid
    || user.business?.businessId
    || user.business?.business_id
  );
};

/**
 * Synchronous best-effort lookup — no network. Returns '' when unknown, and
 * when the only cached name demonstrably belongs to a different business.
 */
export const getCachedBusinessName = (businessId = getBusinessId()) => {
  if (typeof window === 'undefined') return '';

  const wanted = clean(businessId);
  const candidates = [];

  // Loose keys are trustworthy only while the id recorded beside them matches.
  const cachedFor = clean(readLocal(NAME_OWNER_KEY));
  if (!wanted || cachedFor === wanted) {
    candidates.push(...NAME_KEYS.map(readLocal));
  }

  const user = readStoredUser();
  if (user) {
    const owner = getSignedInBusinessId();
    if (!wanted || !owner || owner === wanted) {
      candidates.push(
        user?.businessName,
        user?.business_name,
        user?.restaurantName,
        user?.restaurant_name,
        user?.business?.businessName,
        user?.business?.business_name,
        user?.business?.restaurantName,
        user?.business?.restaurant_name
      );
    }
  }

  return candidates.map(normalizeBusinessName).find(Boolean) || '';
};

const cacheBusinessName = (name, businessId) => {
  if (typeof window === 'undefined' || !name) return;
  try {
    window.localStorage.setItem('businessName', name);
    const owner = clean(businessId);
    if (owner) window.localStorage.setItem(NAME_OWNER_KEY, owner);
  } catch (_error) {
    // Storage full or blocked — the value is still returned to the caller.
  }
};

/* Concurrent exports (or a warm-up racing an export) must not fan out into
   several identical calls — deduped per business so switching accounts mid-run
   still resolves each one. */
const inFlight = new Map();

/* /auth/user only ever describes the signed-in business, so it can answer for
   that business alone. Anything else has to be looked up by id. */
const fetchSignedInBusinessName = async () => {
  const res = await http.get('/auth/user');
  const data = (res && res.data) ? res.data : (res || {});
  return normalizeBusinessName(
    data?.business?.businessName
    || data?.business?.business_name
    || data?.businessName
    || data?.business_name
    || data?.restaurantName
  );
};

const fetchBusinessNameById = async (businessId) => {
  const res = await http.get(`/business/${encodeURIComponent(businessId)}`);
  const data = (res && res.data) ? res.data : (res || {});
  return normalizeBusinessName(data?.name || data?.businessName || data?.business_name);
};

/**
 * Resolve the display name of a specific business, hitting the network only
 * when nothing is cached for that business. Never throws — an unreachable API
 * just yields ''.
 */
export const resolveBusinessName = async (businessId = getBusinessId()) => {
  const wanted = clean(businessId);
  const cached = getCachedBusinessName(wanted);
  if (cached) return cached;
  if (typeof window === 'undefined') return '';

  const key = wanted || '_current';
  if (!inFlight.has(key)) {
    inFlight.set(key, (async () => {
      try {
        const signedInId = getSignedInBusinessId();
        // Ask the endpoint that can actually answer for this business: the
        // session endpoint when it is the signed-in one, the by-id endpoint
        // when a report is being exported for a different business.
        const order = (wanted && signedInId && wanted !== signedInId)
          ? [() => fetchBusinessNameById(wanted)]
          : [fetchSignedInBusinessName, ...(wanted ? [() => fetchBusinessNameById(wanted)] : [])];

        for (const attempt of order) {
          try {
            const name = await attempt();
            if (name) {
              cacheBusinessName(name, wanted);
              return name;
            }
          } catch (_error) {
            // Try the next source before giving up.
          }
        }
        return '';
      } catch (_error) {
        // Offline / expired session / endpoint unavailable — cover falls back to BID.
        return '';
      } finally {
        inFlight.delete(key);
      }
    })());
  }

  return inFlight.get(key);
};

/** Build the cover's "Prepared for" line from an already-known name. */
export const buildPreparedFor = (name, businessId) => {
  const displayName = normalizeBusinessName(name);
  const bid = clean(businessId);

  if (displayName) return bid ? `${displayName} (BID ${bid})` : displayName;
  return bid ? `BID ${bid}` : '';
};

/**
 * The value report covers should print: "Zyca Cafe (BID 136)", degrading to
 * "BID 136" only when the name genuinely cannot be resolved.
 */
export const getPreparedFor = async (businessId = getBusinessId()) => {
  const bid = clean(businessId);
  return buildPreparedFor(await resolveBusinessName(bid), bid);
};

/** Fire-and-forget warm-up so the first export never waits on the network. */
export const primeBusinessName = (businessId = getBusinessId()) => {
  if (typeof window === 'undefined') return;
  if (getCachedBusinessName(businessId)) return;
  resolveBusinessName(businessId).catch(() => {});
};

/** Drop the cached name (call on logout / business switch). */
export const clearCachedBusinessName = () => {
  if (typeof window === 'undefined') return;
  [...NAME_KEYS, NAME_OWNER_KEY].forEach((key) => {
    try {
      window.localStorage.removeItem(key);
    } catch (_error) {
      // Nothing to do — a stale key is corrected on the next resolve.
    }
  });
};
